import Vue from 'vue';
import { Getter, State, Mutation } from 'vuex-simple';
import { BaseModule } from '@/HostApp/store/ApsModules/BaseModule';

import analyticsModule from '@/HostApp/services/Analytics/AnalyticsModule';
import httpServiceModule from '@/HostApp/services/HttpService/HttpServiceModule';
import svgIconsMap, { defaultSvgIcon } from '@/HostApp/store/ApsModules/SvgIconsMap/svgIconsMap';
import { serverProps } from '@/Common/Environment/const';

const CONTEXT_ROOT = 'ccp';

/**
 * @State decorator creates a handler 'propName' : {
 * 		get: () => { return Vuex.store.propName },
 * 		set: (value) => { Vuex.store.commit(propName, value)}
 * 	}
 *
 * 	to make any value from window.aps reactive, just describe it here with decorator @State()
 **/

const concatIconPath = (apsPackage: APS.INavigationTreeItemPackage): string => {
	return `${apsPackage.url}/${apsPackage.id}/${apsPackage.icon}`;
};

export const getIcon = (apsPackage: APS.INavigationTreeItemPackage): string => {
	const iconName = apsPackage.icon;

	if (svgIconsMap[iconName]) {
		return svgIconsMap[iconName];
	} else {
		// check if icon has .png/.svg format
		return iconName.slice(-4, -3) === '.' ? concatIconPath(apsPackage) : iconName;
	}
};

const replaceIcon = (apsPackage: APS.INavigationTreeItemPackage): string => {
	const newIcon = apsPackage?.icon && getIcon(apsPackage);
	return newIcon || defaultSvgIcon;
};

export function getNavId(id: string): string {
	// summary:
	// Get short id of view
	return id
		.replace(/#|http:\/\/www.parallels.com|http:\/\/aps-standard.org/gi, (word) => {
			switch (word) {
				case '#':
					return '/';
				case 'http://www.parallels.com':
					return 'pa';
				case 'http://aps-standard.org':
					return 'aps';
				default:
					return '';
			}
		})
		.replace('http://', '');
}

export class NavigationModule extends BaseModule {
	@State()
	public loadingUpdateNewExperience: boolean = false;

	constructor() {
		super();
	}

	public showWelcomeWizard: boolean =
		window.localStorage.getItem('showWelcomeWizard') === 'true' && !serverProps.isXSClientType;

	public disableWelcomeWizard(): void {
		window.localStorage.setItem('showWelcomeWizard', 'false');
	}

	protected async updateNewExperience(newExperience: boolean): Promise<string | null> {
		const { context } = window.aps;
		const user = context.user as APS.IUserInfo;
		const accountId = user?.organization?.aps?.id;
		const request = accountId
			? (await httpServiceModule.put<string>(`/aps/2/resources/${accountId}/new-experience`, { newExperience }))
					.data
			: null;

		return request;
	}

	public async enableWorkspace(vueWorkspace: boolean): Promise<void> {
		this.setLoadingUpdateNewExperience(true);

		try {
			await this.updateNewExperience(vueWorkspace);
		} catch (error) {
			//TODO add some noti
			// eslint-disable-next-line no-console
			console.error('UpdateNewExperience request is fail: ', error);
			return;
		} finally {
			this.setLoadingUpdateNewExperience(false);
		}

		window.localStorage.setItem('showWelcomeWizard', String(vueWorkspace));

		const transitionDelay = 500;

		const { style } = document.body;

		style.transition = `opacity ${transitionDelay}ms ease`;
		style.opacity = '0';

		setTimeout(() => {
			analyticsModule.pushHostGAData({
				event: 'NE switcher',
				eventCategory: 'NE switcher',
				eventAction: vueWorkspace ? 'ON' : 'OFF'
			});
			window.location.reload();
		}, transitionDelay);
	}

	public onWorkspaceChange(newValue: boolean): void {
		document.body.style.overflow = newValue ? 'hidden auto' : 'hidden';
		document.body.style.backgroundColor = 'var(--background-color-stage)';
	}

	@Getter()
	public get sideMenuItems(): Array<APS.ISideMenuItem> {
		return this.treeItems
			.filter((item) => !item.children || this.hasView(item))
			.map((item) => ({
				id: item.id,
				label: item.label,
				icon: replaceIcon(item.package),
				children: this.storedMenuChildren[item.id],
				useOriginalIcon: item.package?.useOriginalIcon,
				subscriptionId: item.subscriptionId
			}));
	}

	protected async sendNavigationRequest(itemId: string): Promise<{ items: Array<APS.INavigationTreeItem> }> {
		const { context } = window.aps;

		const url = `/aps/2/navigation?bw_id=${context._sessionId || ''}`;

		const parameters = {
			placeholder: serverProps.placeholder,
			context: [],
			parameters: {},
			locale: context._locale,
			itemId
		};

		return (await httpServiceModule.post<{ items: APS.INavigationTreeItem[] }>(url, parameters)).data;
	}

	public async getNavigationResponse(itemId: string): Promise<boolean> {
		try {
			const response = await this.sendNavigationRequest(itemId);
			this.updateSideMenuItems(response?.items || []);
			return true;
		} catch (e) {
			return false;
		}
	}

	public openLink(href: string): void {
		if (href.startsWith('http://') || href.startsWith('https://')) {
			window.open(href, '_blank');
		} else {
			if (href.startsWith('/ccp/signout') || href.startsWith('/ccp/classicpanel')) {
				document.location.href = href;
			} else {
				let url = href.replace('/v/', '/');

				url = getNavId(url);

				if (!url.includes('bw_id=')) {
					url = `${url}?bw_id=${window.aps.context._sessionId}`;
				}

				if (!url.startsWith('/ccp')) {
					url = `/ccp${url}`;
				}

				window.aps.apsc.gotoURL(url);
			}
		}
	}

	//todo avb refactor!
	public doNavigation(item: APS.INavigationTreeItem): void {
		//todo avb check router params
		const getParams: Array<string> = [];

		if (window.aps.context._sessionId) {
			getParams.push(`bw_id=${window.aps.context._sessionId}`);
		}

		const subscription = item.subscriptionId ? `/s/${item.subscriptionId}` : '';
		const params = getParams.length ? `?${getParams.join('&')}` : '';

		const navId = getNavId(item.id);

		const url = `${CONTEXT_ROOT}/i/${navId}${subscription}${params}`;
		window.aps.apsc.gotoURL(url);
	}

	@State()
	protected storedMenuChildren: Common.IMap<Array<APS.ISideMenuItem>> = {};

	@Mutation()
	protected setLoadingUpdateNewExperience(loading: boolean): void {
		this.loadingUpdateNewExperience = loading;
	}

	@State()
	public isMenuCollapsed: boolean = false;

	@State()
	public loadedSubmenu: Map<string, boolean> = new Map();

	@State()
	public vueWorkspace: boolean | null = null;

	@Getter()
	public get isVueWorkspace(): boolean {
		return !!this.vueWorkspace;
	}

	private hasView({ children }: APS.INavigationTreeItem): boolean {
		return children?.some((child) => child.type === 'view' || child.type === 'wizard' || this.hasView(child));
	}

	private isActive({ children }: APS.INavigationTreeItem, viewId: string): boolean {
		return children?.some((child) => {
			if (child.type === 'view' || child.type === 'wizard') {
				return child.id === viewId;
			}
			return this.isActive(child, viewId);
		});
	}

	@Getter()
	public get currentMenuItemId(): string | undefined {
		if (this.treeContext.length) {
			const viewId = this.treeContext[this.treeContext.length - 1].viewId;

			const currentItem = this.treeItems?.find((item) => this.isActive(item, viewId));
			if (currentItem && !this.storedMenuChildren[currentItem.id]) {
				return currentItem.id;
			}

			return currentItem?.children.find((item) => this.isActive(item, viewId))?.id;
		}
		return undefined;
	}

	public updateSideMenuItems(menuItems: Array<APS.INavigationTreeItem>): void {
		const { storedMenuChildren } = this;

		menuItems.forEach((item) => {
			if (item.children?.length) {
				const value = item.children
					.filter((subItem) => subItem.type === 'item')
					.map((subItem) => ({
						id: subItem.id,
						label: subItem.label,
						icon: subItem.package?.icon,
						useOriginalIcon: subItem.package?.useOriginalIcon,
						subscriptionId: subItem.subscriptionId
					}));

				this.loadedSubmenu.set(item.id, true);
				value.length && Vue.set(storedMenuChildren, item.id, value);
			}
		});
	}

	@Getter()
	public get treeItems(): Array<APS.INavigationTreeItem> {
		return this.tree ? this.tree.items : [];
	}

	@Getter()
	public get treeContext(): Array<APS.INavigationTreeContext> {
		return this.tree ? this.tree.context : [];
	}

	@State()
	public backViews: [] = [];

	@State()
	public tree: APS.INavigationTree | null = null;

	@State()
	public helpMenu: APS.IMenuItems | null = null;

	@State()
	public userMenu: APS.IMenuItems | null = null;

	@State()
	public pmsCache: object = {};

	@State()
	public prevState: object = {};

	@State()
	public views: object = {};
}

const navigationModule = new NavigationModule();

export default navigationModule;
